import { type PaymentKeyHash, type UTxO } from '@lucid-evolution/lucid';
import 'reflect-metadata';
import { TRANSACTION_STATUS_FAILED } from '../Commons/Constants/constants';
import { asEntity } from '../Commons/Decorators/Decorator.asEntity';
import { Convertible } from '../Commons/Decorators/Decorator.Convertible';
import { explainErrorTx } from '../Commons/explainError';
import { TransactionDatum, TransactionRedeemer } from '../Commons/types';
import { isEmptyObject, toJson } from '../Commons/utils';
import { BaseEntity } from './Base/Base.Entity';

@asEntity()
export class TransactionEntity extends BaseEntity {
    protected static _apiRoute: string = 'transactions';
    protected static _className: string = 'Transactions';

    // #region fields

    @Convertible()
    hash!: string;

    @Convertible()
    paymentPKH!: PaymentKeyHash;

    @Convertible()
    date!: Date;

    @Convertible()
    type!: string;

    @Convertible()
    status!: string;

    @Convertible()
    error!: Object;

    @Convertible()
    parse_info!: string;

    @Convertible({ type: Object })
    ids!: Record<string, string | undefined>;

    @Convertible({ type: Object })
    redeemers!: Record<string, TransactionRedeemer>;

    @Convertible({ type: Object })
    datums!: Record<string, TransactionDatum>;

    @Convertible({ type: Object })
    consuming_UTxOs!: UTxO[];

    @Convertible({ type: Object })
    reading_UTxOs!: UTxO[];

    @Convertible()
    valid_from?: number;

    @Convertible()
    valid_until?: number;

    @Convertible()
    unit_mem?: number;

    @Convertible()
    unit_steps?: number;

    @Convertible()
    fee?: number;

    @Convertible()
    size?: number;

    @Convertible()
    CBORHex?: string;

    @Convertible({ isCreatedAt: true })
    createdAt!: Date;

    @Convertible({ isUpdatedAt: true })
    updatedAt!: Date;

    // #endregion fields

    // #region  db

    public static defaultFieldsWhenUndefined: Record<string, boolean> = {};

    public static alwaysFieldsForSelect: Record<string, boolean> = {
        ...super.alwaysFieldsForSelect,
        paymentPKH: true,
        date: true,
        type: true,
        hash: true,
        status: true,
        error: true,
        parse_info: true,
        ids: true,
        redeemers: true,
        datums: true,
        consuming_UTxOs: true,
        reading_UTxOs: true,
        valid_from: true,
        valid_until: true,
        unit_mem: true,
        unit_steps: true,
        fee: true,
        CBORHex: true,
        createdAt: true,
        updatedAt: true,
    };

    // #endregion  db

    // #region class methods

    public prettyError(showForUsers = true, showWallet = true, swForExport: boolean = false): string {
        if (this.status !== TRANSACTION_STATUS_FAILED) {
            return '';
        }
        if (isEmptyObject(this.error)) {
            return 'Unknown Error';
        }
        let errorMsg;
        if (showForUsers) {
            errorMsg = explainErrorTx(toJson((this.error as any).error));
        } else {
            errorMsg = (this.error as any).error;
        }
        let res;
        if (showWallet === true) {
            res = toJson(
                {
                    error: errorMsg,
                    wallet: (this.error as any)?.walletInfo ?? '',
                },
                swForExport === false ? ' ' : ''
            );
        } else {
            res = toJson(errorMsg, swForExport === false ? ' ' : '');
        }
        if (swForExport === false) {
            res = res.replace(/,/g, ';');
        }
        return res;
    }

    // #endregion class methods
}
